import { useState } from "react";
import { Overlay } from "../../../ui/atoms/Overlay";
import { ModalActions } from "../../../ui/molecules/ModalActions";

interface RecoveryKeyModalProps {
  readonly recoveryKey: string;
  readonly onAcknowledge: () => void;
}

export function RecoveryKeyModal({ recoveryKey, onAcknowledge }: RecoveryKeyModalProps) {
  const [confirmed, setConfirmed] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(recoveryKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const content = `Palnia — Clé de récupération\n\n${recoveryKey}\n\nConservez ce fichier en lieu sûr.\nCette clé est le seul moyen de récupérer votre compte si vous perdez votre mot de passe.\n`;
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "palnia-cle-recuperation.txt";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAcknowledge = () => {
    localStorage.setItem("palnia_recovery_key_confirmed", String(Date.now()));
    onAcknowledge();
  };

  return (
    <Overlay open onClose={() => {}}>
      <div className="bg-surface rounded-2xl border border-border p-6 w-full max-w-md flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <h2 className="text-lg font-bold text-ink">Clé de récupération</h2>
          <p className="text-sm text-ink-muted leading-relaxed">
            Cette clé est le seul moyen de récupérer votre compte si vous
            perdez votre mot de passe. Notez-la dans un endroit sûr.
            Elle ne sera plus jamais affichée.
          </p>
        </div>

        <div className="bg-surface-alt rounded-xl p-4 flex flex-col gap-3">
          <code className="text-center text-lg font-mono font-bold text-accent tracking-wider break-all select-all">
            {recoveryKey}
          </code>
          <div className="flex gap-2 self-center">
            <button
              type="button"
              onClick={handleCopy}
              className="px-3 py-2 text-xs bg-surface border border-border/60 rounded-md hover:bg-surface-alt text-ink-muted hover:text-ink transition-colors min-h-[44px] touch-feedback"
            >
              {copied ? "Copié !" : "Copier"}
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="px-3 py-2 text-xs bg-accent text-white rounded-md hover:opacity-80 transition-opacity min-h-[44px] touch-feedback"
            >
              Télécharger .txt
            </button>
          </div>
        </div>

        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-xs text-red-400 leading-relaxed font-medium">
            Sans cette clé, vos données seront perdues définitivement si vous oubliez votre mot de passe. Personne ne pourra les récupérer, même l'équipe Palnia.
          </p>
        </div>

        <label className="flex items-start gap-2 cursor-pointer">
          <div className="relative mt-0.5">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="sr-only"
            />
            <div className={`w-4 h-4 rounded-full border-2 transition-all flex items-center justify-center ${
              confirmed
                ? "bg-accent border-accent"
                : "border-border hover:border-accent/50"
            }`}>
              {confirmed && (
                <svg viewBox="0 0 24 24" className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" strokeWidth={3}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </div>
          </div>
          <span className="text-sm text-ink">
            J'ai sauvegardé ma clé de récupération en lieu sûr
          </span>
        </label>

        <ModalActions
          mode="custom"
          onCancel={() => {}}
          customPrimaryAction={{
            label: "Continuer",
            onClick: handleAcknowledge,
            disabled: !confirmed,
            variant: "primary",
          }}
        />
      </div>
    </Overlay>
  );
}
